import type { AbilityDef, ClassId } from "../sim/types";
import { CLASSES } from "./classes";

type PartialAbility = Partial<AbilityDef> &
  Pick<AbilityDef, "id" | "name" | "classId" | "slot" | "description" | "kind">;

function ability(partial: PartialAbility): AbilityDef {
  return {
    cd: 0,
    range: 0,
    target: "none",
    windup: 0,
    windupMove: 1,
    radius: 0,
    arc: 0,
    damage: 0,
    splash: 0,
    heal: 0,
    damageType: "physical",
    onHit: [],
    onCrit: [],
    selfOnCast: [],
    projectileSpeed: 0,
    projectileRadius: 0,
    projectileCount: 1,
    projectileSpread: 0,
    dashDistance: 0,
    chargeSpeed: 0,
    delay: 0,
    zoneKind: "",
    zoneDuration: 0,
    ...partial,
  };
}

export const ABILITIES: Record<string, AbilityDef> = {
  cleave: ability({
    id: "cleave",
    name: "Cleave",
    classId: "warrior",
    slot: "auto",
    description: "3 to the closest foe, 2 to others in the arc. Bleed; Cripple fleeing; Stagger Crippled.",
    kind: "meleeArc",
    target: "foe",
    range: 1.75,
    arc: 1.92,
    damage: 3,
    splash: 2,
    onHit: [
      { op: "status", id: "bleeding", duration: 4 },
      { op: "statusIf", pred: "fleeing", id: "cripple", duration: 3 },
      { op: "statusIf", pred: "hasCripple", id: "stagger", duration: 0.45 },
    ],
  }),
  bulls_charge: ability({
    id: "bulls_charge",
    name: "Bull's Charge",
    classId: "warrior",
    slot: "skill",
    description: "Wind up, then charge. Strike the first foe; Stun it if it is moving.",
    kind: "chargeLine",
    target: "none",
    cd: 8,
    range: 6.2,
    windup: 0.32,
    windupMove: 0.2,
    damage: 2,
    chargeSpeed: 16,
    onHit: [],
  }),
  dismember: ability({
    id: "dismember",
    name: "Dismember",
    classId: "warrior",
    slot: "ult",
    description: "3 damage; Crit if Immobile. Maim and Bleed. On Crit: Wound.",
    kind: "meleeArc",
    target: "foe",
    cd: 18,
    range: 1.85,
    arc: 1.4,
    damage: 3,
    critIf: "immobile",
    onHit: [
      { op: "status", id: "maim", duration: 4 },
      { op: "status", id: "bleeding", duration: 4 },
    ],
    onCrit: [{ op: "status", id: "wound", duration: 5 }],
  }),

  crippling_shot: ability({
    id: "crippling_shot",
    name: "Crippling Shot",
    classId: "ranger",
    slot: "auto",
    description: "1 damage. Bleed and Cripple on hit.",
    kind: "projectile",
    target: "foe",
    range: 8.6,
    damage: 1,
    projectileSpeed: 18,
    projectileRadius: 0.13,
    onHit: [
      { op: "status", id: "bleeding", duration: 4 },
      { op: "status", id: "cripple", duration: 3 },
    ],
  }),
  tumble: ability({
    id: "tumble",
    name: "Tumble",
    classId: "ranger",
    slot: "skill",
    description: "Dash and gain Fleet. Recharges twice as fast while Immobile.",
    kind: "dash",
    target: "none",
    cd: 7,
    range: 3.2,
    dashDistance: 3.2,
    selfOnCast: [{ op: "status", id: "fleet", duration: 1.6 }],
  }),
  heavy_draw: ability({
    id: "heavy_draw",
    name: "Heavy Draw",
    classId: "ranger",
    slot: "ult",
    description: "2 damage. Wound on hit. Stagger Crippled targets.",
    kind: "projectile",
    target: "foe",
    cd: 16,
    range: 9,
    damage: 2,
    projectileSpeed: 20,
    projectileRadius: 0.15,
    onHit: [
      { op: "status", id: "wound", duration: 5 },
      { op: "statusIf", pred: "hasCripple", id: "stagger", duration: 0.45 },
    ],
  }),

  mending_touch: ability({
    id: "mending_touch",
    name: "Mending Touch",
    classId: "priest",
    slot: "auto",
    description: "Heal a nearby unit and remove a Condition. If a Condition is removed, grant Regen.",
    kind: "custom",
    target: "any",
    range: 5.6,
    heal: 2,
  }),
  be_not_afraid: ability({
    id: "be_not_afraid",
    name: "Be Not Afraid",
    classId: "priest",
    slot: "skill",
    description: "Grant an ally Regen and 4 decaying Shield, plus Shield of Faith while it lasts.",
    kind: "custom",
    target: "ally",
    cd: 10,
    range: 7.2,
  }),
  cleanse_evil: ability({
    id: "cleanse_evil",
    name: "Cleanse Evil",
    classId: "priest",
    slot: "ult",
    description: "Mark an area. After a delay, heal 4 and grant Regen; doubled in the center. Crits Burning.",
    kind: "areaDelay",
    target: "none",
    cd: 20,
    range: 7.5,
    radius: 2.6,
    heal: 4,
    delay: 0.7,
    zoneKind: "cleanse_evil",
  }),

  reap: ability({
    id: "reap",
    name: "Reap",
    classId: "cultist",
    slot: "auto",
    description: "Bleed a nearby unit. If already Bleeding, Cursed, or at 2 HP or less, steal 1 Health. Harvest corpses into Skeletons.",
    kind: "custom",
    target: "any",
    range: 1.95,
  }),
  life_transfer: ability({
    id: "life_transfer",
    name: "Life Transfer",
    classId: "cultist",
    slot: "skill",
    description: "Sacrifice up to half your current Health to heal an ally for twice that and grant Regen.",
    kind: "custom",
    target: "ally",
    cd: 8,
    range: 6.5,
  }),
  flesh_of_my_flesh: ability({
    id: "flesh_of_my_flesh",
    name: "Flesh of My Flesh",
    classId: "cultist",
    slot: "ult",
    description: "Sacrifice all of your Health to create a Flesh Golem.",
    kind: "custom",
    target: "self",
    cd: 24,
    range: 0,
  }),

  phantom_pain: ability({
    id: "phantom_pain",
    name: "Phantom Pain",
    classId: "psychic",
    slot: "auto",
    description: "If the target has no Regen: 4 Psychic and grant 3s Regen. Otherwise 1 Psychic.",
    kind: "custom",
    target: "foe",
    range: 6.6,
    damageType: "psychic",
  }),
  feel_my_pain: ability({
    id: "feel_my_pain",
    name: "Feel My Pain",
    classId: "psychic",
    slot: "skill",
    description: "Curse an enemy with Empathy.",
    kind: "target",
    target: "foe",
    cd: 10,
    range: 7.2,
    onHit: [
      { op: "status", id: "empathy", duration: 8 },
      { op: "status", id: "curse", duration: 8 },
    ],
  }),
  blackout: ability({
    id: "blackout",
    name: "Blackout",
    classId: "psychic",
    slot: "ult",
    description: "Interrupt a foe or yourself. Cripple, Blind, Silence, and immune to Psychic.",
    kind: "custom",
    target: "any",
    cd: 18,
    range: 7.5,
  }),

  serpent_fan: ability({
    id: "serpent_fan",
    name: "Serpent Fan",
    classId: "assassin",
    slot: "auto",
    description: "Throw kunai in an arc. 1 damage, Poison. Blind if they face you; Cripple otherwise.",
    kind: "projectile",
    target: "foe",
    range: 4.4,
    damage: 1,
    projectileSpeed: 16,
    projectileRadius: 0.11,
    projectileCount: 5,
    projectileSpread: 0.55,
    onHit: [
      { op: "status", id: "poison", duration: 5 },
      { op: "statusIf", pred: "facingSource", id: "blind", duration: 2.2 },
      { op: "statusIf", pred: "notFacingSource", id: "cripple", duration: 2.5 },
    ],
  }),
  clean_cuts: ability({
    id: "clean_cuts",
    name: "Clean Cuts",
    classId: "assassin",
    slot: "auto",
    description: "2 damage in an arc. Bleed. On Crit: Cripple and Maim.",
    kind: "meleeArc",
    target: "foe",
    range: 1.65,
    arc: 1.7,
    damage: 2,
    onHit: [{ op: "status", id: "bleeding", duration: 4 }],
    onCrit: [
      { op: "status", id: "cripple", duration: 3 },
      { op: "status", id: "maim", duration: 4 },
    ],
  }),
  close_quarters: ability({
    id: "close_quarters",
    name: "Close Quarters",
    classId: "assassin",
    slot: "skill",
    description: "Switch between Kunai and Sword.",
    kind: "swap",
    target: "self",
    cd: 0.4,
    range: 0,
  }),
  smoke_bomb: ability({
    id: "smoke_bomb",
    name: "Smoke Bomb",
    classId: "assassin",
    slot: "ult",
    description: "Smoke that Blinds enemies. Inside it you are Fleet and can vanish without standing still.",
    kind: "zone",
    target: "none",
    cd: 18,
    range: 0,
    radius: 2.4,
    zoneKind: "smoke",
    zoneDuration: 5.5,
  }),

  scythe: ability({
    id: "scythe",
    name: "Mystic Sweep",
    classId: "dervish",
    slot: "auto",
    description: "Broad melee arc. Crits foes that are not Enchanted. On hit, gain a short Enchantment.",
    kind: "meleeArc",
    target: "foe",
    range: 2.15,
    arc: 2.15,
    damage: 2,
    critIf: "notEnchanted",
    onHit: [],
    selfOnCast: [{ op: "status", id: "mystic_sweep", duration: 3 }],
  }),
  right_from_wrong: ability({
    id: "right_from_wrong",
    name: "Right From Wrong",
    classId: "dervish",
    slot: "skill",
    description: "Nearby enemies lose positive Enchantments. Nearby allies lose Conditions.",
    kind: "custom",
    target: "none",
    cd: 10,
    range: 0,
    radius: 3.6,
  }),
  avatar: ability({
    id: "avatar",
    name: "Avatar",
    classId: "dervish",
    slot: "ult",
    description: "Powerful self-Enchantment. Shared Fate copies it to nearby units.",
    kind: "self",
    target: "self",
    cd: 22,
    range: 0,
    selfOnCast: [
      { op: "status", id: "avatar", duration: 6 },
      { op: "status", id: "regen", duration: 6 },
    ],
  }),

  lesser_invocation: ability({
    id: "lesser_invocation",
    name: "Lesser Invocation",
    classId: "ritualist",
    slot: "auto",
    description: "Heal an ally and grant Regen, or damage an enemy and inflict Burning. Spirits are valid.",
    kind: "custom",
    target: "any",
    range: 7.2,
    damage: 2,
    heal: 2,
    damageType: "physical",
  }),
  invocation: ability({
    id: "invocation",
    name: "Invocation",
    classId: "ritualist",
    slot: "skill",
    description: "Ally: heal and damage nearby enemies. Enemy: damage and heal nearby allies. Spirits count.",
    kind: "custom",
    target: "any",
    cd: 9,
    range: 7.4,
    radius: 2.6,
    damage: 3,
    heal: 3,
  }),
  soul_cleave: ability({
    id: "soul_cleave",
    name: "Soul Cleave",
    classId: "ritualist",
    slot: "ult",
    description: "An ally or Spirit loses half its Health. A Spirit is born with that Health and pulses the mixed Invocation.",
    kind: "custom",
    target: "allyOrSpirit",
    cd: 20,
    range: 7.4,
    radius: 2.8,
  }),

  spear: ability({
    id: "spear",
    name: "Javelin",
    classId: "paragon",
    slot: "auto",
    description: "Ranged physical. Extra damage vs Retribution. Crits low-Health foes.",
    kind: "projectile",
    target: "foe",
    range: 7.4,
    damage: 2,
    bonusIf: { pred: "hasRetribution", amount: 1 },
    critIf: "lowHp",
    projectileSpeed: 17,
    projectileRadius: 0.13,
  }),
  rally: ability({
    id: "rally",
    name: "Rally",
    classId: "paragon",
    slot: "skill",
    description: "Allies deal more, take 1 less, and receive a small party heal weighted to the wounded.",
    kind: "custom",
    target: "none",
    cd: 12,
    range: 0,
    radius: 4.5,
    heal: 1,
  }),
  turn_the_tide: ability({
    id: "turn_the_tide",
    name: "Turn the Tide",
    classId: "paragon",
    slot: "ult",
    description: "Allies gain Fleet, Frenzy, a small heal, and damage reduction.",
    kind: "custom",
    target: "none",
    cd: 22,
    range: 0,
    radius: 4.8,
    heal: 2,
  }),

  hammerfall: ability({
    id: "hammerfall",
    name: "Hammerfall",
    classId: "paladin",
    slot: "auto",
    description: "Hammer strike. Spends Charge for bonus damage. At full Charge: area damage and Stun.",
    kind: "custom",
    target: "foe",
    range: 1.85,
    arc: 1.5,
    damage: 2,
  }),
  sanctuary: ability({
    id: "sanctuary",
    name: "Sanctuary",
    classId: "paladin",
    slot: "skill",
    description: "Enchant yourself and nearby allies with Shield, Regen, and a small heal. Stronger on you.",
    kind: "custom",
    target: "none",
    cd: 12,
    range: 0,
    radius: 3.4,
    heal: 2,
  }),
  hallowed_ground: ability({
    id: "hallowed_ground",
    name: "Hallowed Ground",
    classId: "paladin",
    slot: "ult",
    description: "Visible windup, then slam. Stun, raise a ring, and hallow the ground. Allies inside gain Regen and reduction.",
    kind: "chargeLine",
    target: "none",
    cd: 24,
    range: 5.5,
    radius: 3.2,
    windup: 0.75,
    windupMove: 0,
    damage: 2,
    zoneKind: "hallowed",
    zoneDuration: 6,
  }),

  minion_slash: ability({
    id: "minion_slash",
    name: "Slash",
    classId: "skeleton",
    slot: "auto",
    description: "A bone-edged cut.",
    kind: "meleeArc",
    target: "foe",
    range: 1.5,
    arc: 1.4,
    damage: 1,
  }),
  minion_slam: ability({
    id: "minion_slam",
    name: "Slam",
    classId: "golem",
    slot: "auto",
    description: "A heavy fist.",
    kind: "meleeArc",
    target: "foe",
    range: 1.7,
    arc: 1.5,
    damage: 2,
  }),
};

export function abilityById(id: string): AbilityDef {
  const a = ABILITIES[id];
  if (!a) throw new Error(`Unknown ability ${id}`);
  return a;
}

export function autoFor(unitClass: ClassId, weapon = 0): string {
  const autos = CLASSES[unitClass].autos;
  if (!autos.length) return "";
  return autos[Math.max(0, Math.min(weapon, autos.length - 1))] ?? "";
}
